import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import WeeklySummary from '../components/WeeklyNews/WeeklySummary';
import DaySection from '../components/DailyNews/DaySection';
import Loading from '../components/Common/Loading';
import { PageErrorBoundary } from '../components/Common/ErrorBoundary';
import { WeeklySummaryData } from '../types/weekly';
import { DailySummaryData } from '../components/DailyNews/DailySummary';
import { Article } from '../config/api';
import { dailySummaryService } from '../services/dailySummaryService';
import { articleService } from '../services/articleService';
import { weeklySummaryService } from '../services/weeklySummaryService';
import './Home.css';

const WeeklySummaryDetail: React.FC = () => {
  const { weekId } = useParams<{ weekId: string }>();
  const navigate = useNavigate();
  const [weeklySummary, setWeeklySummary] = useState<WeeklySummaryData | null>(null);
  const [dailySummaries, setDailySummaries] = useState<DailySummaryData[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [allWeeklySummaries, setAllWeeklySummaries] = useState<WeeklySummaryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWeeklyArchive = async () => {
      if (!weekId) return;
      
      try {
        setLoading(true);
        
        // Fetch weekly summary
        const summary = await weeklySummaryService.getWeeklySummaryById(parseInt(weekId));
        if (!summary) {
          setError('notFound');
          return;
        }
        setWeeklySummary(summary);
        
        // Fetch all weekly summaries to determine navigation
        const allWeekliesData = await weeklySummaryService.getWeeklySummaries({ limit: 100 });
        const allSummaries = allWeekliesData.data || [];
        setAllWeeklySummaries(allSummaries);
        
        // Fetch daily summaries for this week
        const dailyResponse = await dailySummaryService.getDailySummariesByWeek(weekId);
        const weekDailySummaries = dailyResponse || [];
        console.log('📅 Daily summaries for week:', weekDailySummaries);
        setDailySummaries(weekDailySummaries);
        
        // Fetch articles for the daily summaries in this week
        if (weekDailySummaries.length > 0) {
          const dailySummaryIds = weekDailySummaries.map(ds => ds.id);
          console.log('🔍 Looking for articles with daily summary IDs:', dailySummaryIds);
          const articlesResponse = await articleService.getArticles({
            dailySummaryIds,
            limit: 1000,
            status: 'active',
          });
          setArticles(articlesResponse.data || []);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching weekly archive:', err);
        setError('Failed to load weekly archive. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchWeeklyArchive();
  }, [weekId]);

  const handleNewerWeek = () => {
    if (!weeklySummary || !allWeeklySummaries.length) return;
    
    // Find newer week by comparing week_start_date
    const currentWeekStart = new Date(weeklySummary.week_start_date);
    const newerWeeks = allWeeklySummaries.filter(ws => 
      new Date(ws.week_start_date) > currentWeekStart
    ).sort((a, b) => new Date(a.week_start_date).getTime() - new Date(b.week_start_date).getTime());
    
    if (newerWeeks.length > 0) {
      // Get the closest newer week (smallest date that's still greater)
      navigate(`/archives/week/${newerWeeks[0].id}`);
    }
  };

  const handleOlderWeek = () => {
    if (!weeklySummary || !allWeeklySummaries.length) return;
    
    // Find older week by comparing week_start_date
    const currentWeekStart = new Date(weeklySummary.week_start_date);
    const olderWeeks = allWeeklySummaries.filter(ws => 
      new Date(ws.week_start_date) < currentWeekStart
    ).sort((a, b) => new Date(b.week_start_date).getTime() - new Date(a.week_start_date).getTime());
    
    if (olderWeeks.length > 0) {
      // Get the closest older week (largest date that's still smaller)
      navigate(`/archives/week/${olderWeeks[0].id}`);
    }
  };

  // Helper functions to check if navigation is available
  const hasNewerWeek = () => {
    if (!weeklySummary || !allWeeklySummaries.length) return false;
    const currentWeekStart = new Date(weeklySummary.week_start_date);
    return allWeeklySummaries.some(ws => new Date(ws.week_start_date) > currentWeekStart);
  };

  const hasOlderWeek = () => {
    if (!weeklySummary || !allWeeklySummaries.length) return false;
    const currentWeekStart = new Date(weeklySummary.week_start_date);
    return allWeeklySummaries.some(ws => new Date(ws.week_start_date) < currentWeekStart);
  };

  if (loading) return <Loading />;
  
  if (error === 'notFound') {
    return (
      <PageErrorBoundary>
        <div className="home-container">
          <div className="home-header">
            <h1>Weekly Archive Not Found</h1>
          </div>
          <main className="home-content">
            <div className="main-content">
              <div style={{ 
                textAlign: 'center', 
                padding: '3rem 1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>📚</div>
                <h3 style={{ color: '#495057', marginBottom: '0.5rem' }}>Weekly Archive Not Found</h3>
                <p style={{ color: '#6c757d', lineHeight: 1.6, marginBottom: '2rem' }}>
                  The weekly archive you're looking for doesn't exist or may have been removed.
                </p>
                <button
                  onClick={() => navigate('/archives')}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  ← Back to Archives
                </button>
              </div>
            </div>
          </main>
        </div>
      </PageErrorBoundary>
    );
  }
  
  if (error) {
    return (
      <PageErrorBoundary>
        <div className="home-container">
          <div className="home-header">
            <h1>Error Loading Archive</h1>
          </div>
          <main className="home-content">
            <div className="main-content">
              <div style={{ 
                textAlign: 'center', 
                padding: '3rem 1rem',
                backgroundColor: '#fff5f5',
                borderRadius: '12px',
                border: '1px solid #fed7d7'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>⚠️</div>
                <h3 style={{ color: '#c53030', marginBottom: '0.5rem' }}>Something Went Wrong</h3>
                <p style={{ color: '#744210', lineHeight: 1.6, marginBottom: '2rem' }}>
                  We encountered an error while loading this weekly archive. Please try again later.
                </p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <button
                    onClick={() => window.location.reload()}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#c53030',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => navigate('/archives')}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    Back to Archives
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </PageErrorBoundary>
    );
  }
  
  if (!weeklySummary) return <Loading />;

  return (
    <PageErrorBoundary>
      <div className="home-container">
        <div className="home-header">
          <h1>{weeklySummary.title}</h1>
          <p className="tagline">
            {new Date(weeklySummary.week_start_date).toLocaleDateString('en-US', { 
              weekday: 'long', month: 'long', day: 'numeric' 
            })} - {new Date(weeklySummary.week_end_date).toLocaleDateString('en-US', { 
              weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
            })}
          </p>
        </div>

        <main className="home-content">
          <div className="main-content">
            {/* Weekly Summary Section */}
            <section className="home-section">
              <WeeklySummary 
                weeklySummary={weeklySummary}
                showThemes={true}
                showStats={true}
              />
            </section>

            {/* Daily Summaries */}
            {dailySummaries.map((dailySummary) => {
              const dayArticles = articles.filter(article => {
                console.log(`🔍 Comparing article.daily_summary_id (${typeof article.daily_summary_id}) ${article.daily_summary_id} === dailySummary.id (${typeof dailySummary.id}) ${dailySummary.id}`);
                return article.daily_summary_id === dailySummary.id;
              });
              console.log(`📰 Daily summary ${dailySummary.id} has ${dayArticles.length} articles:`, dayArticles.map(a => a.title));
              return (
                <section key={dailySummary.id} className="home-section">
                  <DaySection 
                    dailySummary={dailySummary}
                    articles={dayArticles}
                    defaultExpanded={false}
                    showThemes={true}
                    showStats={true}
                  />
                </section>
              );
            })}

            {/* Navigation */}
            <section className="home-section">
              <div className="week-navigation" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '2rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginTop: '2rem'
              }}>
                <div style={{ flex: 1 }}>
                  {hasNewerWeek() && (
                    <button
                      onClick={handleNewerWeek}
                      className="nav-button"
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '16px'
                      }}
                    >
                      ← Newer Week
                    </button>
                  )}
                </div>
                
                <button
                  onClick={() => navigate('/archives')}
                  className="nav-button"
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  Back to Archives
                </button>
                
                <div style={{ flex: 1, textAlign: 'right' }}>
                  {hasOlderWeek() && (
                    <button
                      onClick={handleOlderWeek}
                      className="nav-button"
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '16px'
                      }}
                    >
                      Older Week →
                    </button>
                  )}
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </PageErrorBoundary>
  );
};

export default WeeklySummaryDetail;
